import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Upload, X, ChevronDown, Ticket } from 'lucide-react';
import TicketBoard from './TicketBoard';
import MiniBoard from './MiniBoard';
import TicketTiles from './TicketTiles';
import RepeatedPicks from './RepeatedPicks';
import TrackerLegend from './TrackerLegend';
import ActiveScoreboard from './ActiveScoreboard';
import ActiveProgress from './ActiveProgress';
import { WindowGroups } from './MobileTracker';
import { SortBar, useTicketSort } from './SortBar';
import { compareTickets } from './ticketDate';
import { TicketApi } from '../lib/ticketApi';
import { postUpload, pollUploadJob, cancelJob, keepAwake } from '../lib/uploadJobs';

const ID_KEY = 'nicks_guest_id';
const VIEW_KEY = 'nicks_guest_view';
const deviceId = () => {
  let id = localStorage.getItem(ID_KEY);
  if (!id) { id = (crypto.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}-guest`); localStorage.setItem(ID_KEY, id); }
  return id;
};

const STEPS = [
  ['Open your sportsbook app', 'FanDuel, Kalshi, DraftKings, etc. Go to My Bets → Open, and open the ticket you want to track.'],
  ['Take a screenshot or screen recording', 'Screenshot shows the whole ticket? Use that. Long ticket (lots of legs)? Take a few screenshots while scrolling, or one screen recording scrolling from the top of the ticket to the bottom (wager and payout showing).'],
  ['Tap "Upload my tickets"', 'Pick the screenshots and/or recordings (up to 6 at a time). Several tickets at once is fine — each one is read separately.'],
  ['Wait for it to read', 'About 5 seconds per screenshot, about a minute per recording. You can switch apps — it keeps going. Tap Stop to cancel.'],
  ['Track it live', 'Each leg lights up: green = hit, blue = on pace, yellow = behind, red = lost. Scores, clock, player stats, fouls and injuries update about every 30 seconds.'],
  ['Fix or finish', 'Tap the pencil to fix the bet / payout, Mark won / Mark lost if needed, the archive box to move a finished ticket to Settled, or the trash can to delete it.'],
];

const Directions = () => {
  const [open, setOpen] = useState(() => !localStorage.getItem(`${ID_KEY}_seen`));
  useEffect(() => { localStorage.setItem(`${ID_KEY}_seen`, '1'); }, []);
  return (
    <div className="rounded-xl border border-amber-500/40 bg-amber-500/5 p-3" data-testid="guest-directions">
      <button type="button" onClick={() => setOpen(!open)} className="flex w-full items-center justify-between text-left" data-testid="guest-directions-toggle">
        <span className="text-sm font-bold text-amber-200">How to upload &amp; track your tickets</span>
        <ChevronDown className={`h-4 w-4 text-amber-300 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <ol className="mt-2 space-y-2" data-testid="guest-directions-steps">
          {STEPS.map(([h, t], i) => (
            <li key={h} className="flex gap-2">
              <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-amber-500 text-[11px] font-black text-black">{i + 1}</span>
              <p className="text-[12px] text-zinc-300"><b className="text-white">{h}.</b> {t}</p>
            </li>
          ))}
          <li className="text-[11px] text-zinc-500">Your tickets are private and saved on this device only. Up to 10 uploads a day. Finished tickets clear out 7 days after the games end.</li>
        </ol>
      )}
    </div>
  );
};

const VIEWS = [['tiles', 'All tickets'], ['two', 'All-in-one board'], ['three', 'Board · 3 per row'], ['mini', 'Mini cards'], ['settled', 'Settled']];

// Nick's Picks visitors: upload your own slips and track them live — the same board as the owner's, private to this device.
export default function GuestTracker({ token, code }) {
  const api = useMemo(() => ({ base: `${process.env.REACT_APP_BACKEND_URL}/api/public/tracker/${token}`, headers: { 'X-Access-Code': code || '', 'X-Guest-Id': deviceId() } }), [token, code]);
  const [list, setList] = useState([]);
  const [tick, setTick] = useState(0);
  const sortMode = useTicketSort();
  const [busy, setBusy] = useState('');
  const [view, setView] = useState(() => localStorage.getItem(VIEW_KEY) || 'two');
  const ctrl = useRef(null);
  const jobs = useRef(new Set());
  const input = useRef(null);

  const load = useCallback(async () => {
    try {
      setList((await axios.get(`${api.base}/parlays`, { headers: api.headers })).data.parlays || []);
      setTick((n) => n + 1);
    } catch { /* keep the last board */ }
  }, [api]);
  useEffect(() => {
    load();
    const t = setInterval(() => { if (!document.hidden) load(); }, 30000);
    const vis = () => { if (!document.hidden) load(); };
    document.addEventListener('visibilitychange', vis);
    return () => { clearInterval(t); document.removeEventListener('visibilitychange', vis); };
  }, [load]);

  const upload = async (files) => {
    if (!files.length) return;
    if (files.length > 6) { toast.error('Up to 6 files at a time'); return; }
    const c = new AbortController();
    ctrl.current = c;
    setBusy(`Reading ${files.length} file${files.length > 1 ? 's' : ''}…`);
    const lock = await keepAwake();
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append('files', f));
      const key = crypto.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}-up`;
      jobs.current.add(key);  // Stop can cancel even before the server answers
      const { job_id: id } = await postUpload(fd, c.signal, { ...api, headers: { ...api.headers, 'X-Upload-Key': key } });
      const r = await pollUploadJob(id, c.signal, api);
      jobs.current.delete(id);
      if (r.saved) toast.success(`Tracking ${r.saved} new ticket${r.saved > 1 ? 's' : ''}`);
      if (r.duplicates) toast.warning(`${r.duplicates} ticket${r.duplicates > 1 ? 's were' : ' was'} already uploaded — not added twice`);
      if (!r.saved && !r.duplicates) toast.error(`No ticket found${r.errors?.length ? `: ${r.errors.join('; ')}` : ' — try a clearer screenshot'}`);
      load();
    } catch (e) {
      if (!e.stopped) toast.error(e.response?.data?.detail || e.message);
    } finally {
      lock?.release?.().catch(() => {});
      setBusy('');
    }
  };
  const stop = () => {
    ctrl.current?.abort();
    jobs.current.forEach((id) => cancelJob(id, api));
    jobs.current.clear();
    setBusy('');
    toast.info('Stopped');
  };

  const act = (fn, ok) => async (...a) => {
    try { await fn(...a); if (ok) toast.success(typeof ok === 'function' ? ok(...a) : ok); load(); } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };
  const onDelete = async (t) => {
    if (!window.confirm('Delete this ticket?')) return;
    await act(() => axios.delete(`${api.base}/parlays/${t.id}`, { headers: api.headers }), 'Ticket deleted')();
  };
  const onArchive = act((t, archived) => axios.post(`${api.base}/parlays/${t.id}/archive`, { archived }, { headers: api.headers }), (t, a) => (a ? 'Moved to Settled' : 'Back on the board'));
  const onSettle = act((t, result) => axios.post(`${api.base}/parlays/${t.id}/settle`, { result }, { headers: api.headers }), (t, r) => (r === 'auto' ? 'Back to automatic grading' : `Marked ${r.toUpperCase()}`));

  const settled = view === 'settled';
  const shown = list.filter((t) => !!t.archived === settled).sort((a, b) => compareTickets(a, b, sortMode));
  const props = { tickets: shown, onDelete, onArchive, onSettle, onChanged: load };
  return (
    <TicketApi.Provider value={api}>
      <section className="space-y-3" data-testid="guest-tracker">
        <div className="flex items-center gap-2">
          <Ticket className="h-5 w-5 text-amber-400" />
          <h2 className="text-lg font-black text-white">Track your tickets</h2>
        </div>
        <Directions />
        <input ref={input} type="file" multiple accept="image/*,video/*" className="hidden" data-testid="guest-file-input"
          onChange={(e) => { upload([...e.target.files]); e.target.value = ''; }} />
        {busy ? (
          <div className="flex flex-wrap items-center gap-2 rounded-xl border border-zinc-700 bg-zinc-900 p-3" data-testid="guest-uploading">
            <Loader2 className="h-4 w-4 animate-spin text-amber-400" />
            <span className="text-sm text-white">{busy}</span>
            <span className="text-[11px] text-emerald-300">Keeps reading if you switch apps.</span>
            <button type="button" onClick={stop} className="ml-auto inline-flex items-center gap-1 rounded-lg border border-red-500/60 px-3 py-1.5 text-xs font-bold text-red-300 hover:bg-red-500/10" data-testid="guest-stop-btn">
              <X className="h-3.5 w-3.5" /> Stop loading
            </button>
          </div>
        ) : (
          <button type="button" onClick={() => input.current?.click()} data-testid="guest-upload-btn"
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-amber-500 py-3 text-sm font-black text-black transition-transform hover:bg-amber-400 active:scale-[0.99]">
            <Upload className="h-4 w-4" /> Upload my tickets<span className="hidden sm:inline">&nbsp;(screenshots or screen recordings)</span>
          </button>
        )}
        {list.some((t) => !t.archived) && (
          <>
            <ActiveScoreboard reloadKey={tick} />
            <ActiveProgress tickets={list} />
            {!settled && <RepeatedPicks tickets={shown} />}
            {!settled && <SortBar />}
          </>
        )}
        <div className="flex flex-wrap gap-1.5" data-testid="guest-view-toggle">
          {VIEWS.map(([v, label]) => (
            <button key={v} type="button" onClick={() => { setView(v); localStorage.setItem(VIEW_KEY, v); }} data-testid={`guest-view-${v}`}
              className={`rounded-full border px-3 py-1 text-xs font-semibold ${view === v ? 'border-amber-500 bg-amber-500 text-black' : 'border-zinc-700 text-zinc-400 hover:text-white'}`}>
              {label}{v === 'settled' ? ` (${list.filter((t) => t.archived).length})` : ''}
            </button>
          ))}
        </div>
        {!!shown.length && <TrackerLegend />}
        {!shown.length ? (
          <p className="rounded-xl border border-dashed border-zinc-700 p-6 text-center text-sm text-zinc-500" data-testid="guest-empty">
            {settled ? 'No settled tickets yet.' : 'No tickets yet — upload a screenshot or recording of your ticket to start tracking.'}
          </p>
        ) : settled ? <TicketBoard {...props} archived />
          : view === 'tiles' ? <TicketTiles tickets={shown} onChanged={load} renderFull={(p) => <TicketBoard {...props} tickets={[p]} layout="single" />} />
          : <WindowGroups tickets={shown} mode={sortMode} render={(l) => (view === 'mini' ? <MiniBoard {...props} tickets={l} /> : <TicketBoard {...props} tickets={l} layout={view === 'three' ? 'three' : 'two'} />)} />}
      </section>
    </TicketApi.Provider>
  );
}
